import {
  Button,
  Collapse,
  FilePreview,
  getNextTypography,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  useMediaQuery
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import { useClientOptions } from '@paintscout/ui-business'
import type { FeatureProduct } from '@paintscout/util/builder'
import {
  applyProductLayoutOrder,
  getFeatureProductFile,
  getQuoteFeatureProducts,
  isProductHidden,
  moveFeatureProduct,
  setProductHidden,
  setProductOrder,
  withoutHiddenProducts
} from '@paintscout/util/builder'
import classNames from 'classnames'
import { useFlags } from 'shared/components/common/FlagsProvider'
import React, { useEffect, useState } from 'react'
import type { SortableContainerProps, SortableElementProps, SortEndHandler } from 'react-sortable-hoc'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { useQuote } from '../context'
import HideAmountButton from './HideAmountButton'
import OutlinedTableCard from './OutlinedTableCard'

const paintCanSize = 72
const maxProductImageHeight = 140

const SW_LOGO = 'https://res.cloudinary.com/taptapquote/image/upload/v1751660780/paintscout/s-w-logo.png'
const SW_LOGO_XS = 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/s-w-icon.png'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const type = getNextTypography(theme)
    return {
      root: {
        '@media print': {
          display: 'none'
        }
      },
      // Template only: the same inactive dashed box the document header uses for its columns, saying
      // where an estimate's products come from instead of listing any.
      templateNote: {
        margin: theme.spacing(2, 1.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 52,
        padding: theme.spacing(1.5),
        border: `1px dashed ${theme.paintscout.palette.brand.Neutral.P85}`,
        borderRadius: theme.borderRadius.lg,
        color: theme.paintscout.palette.brand.Neutral.P70,
        fontSize: theme.typography.pxToRem(14),
        textAlign: 'center'
      },
      // The provider sub-header ("Sherwin-Williams" + logo) — a lighter band under the card header.
      groupHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minHeight: 44,
        padding: theme.spacing(0.5, 2),
        borderBottom: `1px solid ${neutral.P90}`
      },
      groupLabel: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P35
      },
      logo: {
        height: 28,
        objectFit: 'contain',
        flexShrink: 0
      },
      // Positioned, with the rows as its direct children, so react-sortable-hoc's `offsetTop` sum
      // measures each row exactly (the trap OutlinedItemTable corrects in handleSortMeasure, ENG-2021).
      rows: {
        position: 'relative'
      },
      row: {
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(3),
        padding: theme.spacing(2.5, 2),
        '&:not(:last-child)': {
          borderBottom: `1px solid ${neutral.P90}`
        },
        [theme.breakpoints.down('xs')]: {
          gap: theme.spacing(2)
        }
      },
      // Editor only: the row is a drag target with a hide control on its right, so it tints on hover
      // and leaves room for that control.
      rowEditable: {
        paddingRight: theme.spacing(6),
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: neutral.P98
        }
      },
      // A hidden product stays in the editor, dimmed, so it can be unhidden (ENG-2240).
      rowHidden: {
        '& $filePreview, & $productDetails': {
          opacity: 0.4
        }
      },
      // The same decorative top-center affordance as OutlinedItemTable's rows: the whole row is the
      // drag target, so this only hints at it and never takes a pointer event.
      dragHandle: {
        position: 'absolute',
        top: theme.spacing(0.5),
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        color: neutral.P70,
        pointerEvents: 'none',
        opacity: 0,
        transition: 'opacity 150ms ease',
        '& svg': {
          fontSize: 20
        },
        '$row:hover &, $draggingHelper &': {
          opacity: 1
        },
        '@media print': {
          display: 'none'
        }
      },
      // Hide/unhide, top-right, revealed on row hover like the drag handle (ENG-2240).
      hideControl: {
        position: 'absolute',
        top: theme.spacing(1.5),
        right: theme.spacing(1.5),
        opacity: 0,
        transition: 'opacity 120ms ease',
        '$row:hover &, &:focus-within': {
          opacity: 1
        },
        // The lifted row is being moved, not edited.
        '$draggingHelper &': {
          opacity: 0
        },
        '@media print': {
          display: 'none'
        }
      },
      hideButton: {
        padding: 2,
        color: neutral.P60,
        '& svg': { fontSize: 18 }
      },
      draggingHelper: {
        zIndex: 2_147_483_002,
        background: neutral.White,
        boxShadow: theme.shadows[3],
        // the sortable helper is detached from the card, so give it the row borders back
        border: `1px solid ${neutral.P90}`,
        borderRadius: theme.borderRadius.md
      },
      filePreview: {
        maxWidth: paintCanSize,
        minWidth: paintCanSize,
        minHeight: paintCanSize,
        flexShrink: 0,
        // Cap tall images so the box tops out at maxProductImageHeight (FilePreview adds 2px padding a side)
        '& img': {
          maxHeight: maxProductImageHeight - 4
        }
      },
      productDetails: {
        minWidth: 0
      },
      // `display: block` because both of these render as spans, not their variant's default tag —
      // see the `component="span"` note on the Typography calls below.
      productLabel: {
        display: 'block',
        ...type.rowTitle,
        marginBottom: theme.spacing(1)
      },
      description: {
        display: 'block',
        ...type.body,
        whiteSpace: 'pre-wrap'
      },
      viewMore: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(1),
        borderTop: `1px solid ${neutral.P90}`
      },
      viewMoreButton: {
        fontFamily: theme.typography.button.fontFamily,
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500
      }
    }
  },
  { name: 'ProductsTableNext' }
)

export interface ProductsTableNextProps {
  collapsed?: boolean
  inPresentation?: boolean
  onToggle: (ev: React.MouseEvent) => any
}

/**
 * The "Products" (feature products) section for the new (estimateNext) pipeline. A fresh design built
 * on the shared `OutlinedTableCard`: a "Products" header with the Hide/Show pill, and a "Sherwin-Williams"
 * provider sub-header (with the SW logo) only while every listed product is SW; the company's own
 * custom products (ENG-2237) have none. One row per product (thumbnail + name + description). Reads
 * the same `getQuoteFeatureProducts` data and saved order as the legacy `ProductsTable`.
 *
 * In the editor, each row can be dragged anywhere in the list, across SW and custom products
 * (ENG-2237), and hidden with a hover-revealed eye (ENG-2240). Both persist on `quote.productLayout`;
 * hidden products are dimmed in the editor and left out everywhere else.
 */
export default function ProductsTableNext({ collapsed, inPresentation, onToggle }: ProductsTableNextProps) {
  const classes = useStyles()
  const { quote, isEditable, isCustomerView, updateQuote } = useQuote()
  const { options } = useClientOptions()
  const isXs = useMediaQuery('xs')
  const [isDragging, setDragging] = useState(false)

  // Only the editor can rearrange or hide products. Everywhere else (presentation, customer view,
  // the preview dialog, a read-only estimate) hidden products are simply left out.
  const canEditLayout = !!isEditable && !inPresentation && !isCustomerView

  // The estimator's saved drag order, which may mix SW and custom products; SW first by default.
  const { customProductVisuals } = useFlags()
  const allItems = applyProductLayoutOrder(
    quote,
    getQuoteFeatureProducts({ quote, options, includeCustomProducts: !!customProductVisuals })
  )
  const items = canEditLayout ? allItems : withoutHiddenProducts(quote, allItems)
  // A template has no products of its own: which ones an estimate lists follows from the account's
  // settings and the estimate's type, so the section states that rather than listing nothing
  // (ENG-1282). The header and its Hide/Show stay, as they do in the prototype.
  const isTemplate = quote?.type === 'quoteTemplate'
  const show = !collapsed && (items.length > 0 || isTemplate)

  const [showAll, setShowAll] = useState(false)
  const visibleItems = showAll ? items : items.slice(0, 3)
  // The SW band (name + logo) only heads an all-SW list. Once a custom product is listed (ENG-2237)
  // the band goes, and the card header's own bottom divider sits directly over the first row.
  const showSWHeader = !items.some((item) => !item.featureProduct)

  // Same body-level treatment as OutlinedItemTable: a grabbing cursor while a row is lifted, and no
  // native text selection growing across the rows it passes over (ENG-2000).
  useEffect(() => {
    if (!isDragging) {
      return
    }
    document.body.style.cursor = 'grabbing'
    document.body.style.userSelect = 'none'
    document.body.style.webkitUserSelect = 'none'
    return () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
    }
  }, [isDragging])

  // SW and custom products sort as one list (ENG-2237). Indices are within the shown rows; with
  // "View More" closed, the unshown rows keep their place after.
  const handleSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    setDragging(false)
    if (oldIndex === newIndex) {
      return
    }
    const ordered = moveFeatureProduct({ products: items, shown: visibleItems.length, oldIndex, newIndex })
    updateQuote({ quote: setProductOrder({ quote, keys: ordered.map((item) => item.key) }) })
  }

  const handleToggleHidden = (item: FeatureProduct, hidden: boolean) => {
    updateQuote({ quote: setProductHidden({ quote, key: item.key, hidden }) })
  }

  const renderRow = (item: FeatureProduct, index: number) => {
    const file = getFeatureProductFile(item)
    const hidden = isProductHidden(quote, item.key)
    return (
      <SortableRow
        key={item.key ?? index}
        index={index}
        disabled={!canEditLayout}
        className={classNames(classes.row, {
          [classes.rowEditable]: canEditLayout,
          [classes.rowHidden]: canEditLayout && hidden
        })}
      >
        {canEditLayout && (
          <div className={classes.dragHandle} aria-hidden>
            <DragHandleIcon />
          </div>
        )}
        {file && <FilePreview classes={{ root: classes.filePreview }} file={file} />}
        <div className={classes.productDetails}>
          {/* `component="span"` is load-bearing on both of these: the document root
            spreads getPresentationContentStyles, whose `& h3` and `& p` rules set
            `color: inherit`. Those are descendant selectors (0-1-1), so they outrank
            these single classes (0-1-0) and the labels silently lost their neutrals to
            whatever the document inherited. Same fix as OutlinedTableCard's section
            subtitle. Their `display: block` comes from the classes. */}
          <Typography variant="h3" component="span" className={classes.productLabel}>
            {item.label}
          </Typography>
          <Typography variant="body1" component="span" className={classes.description}>
            {item.description}
          </Typography>
        </div>
        {canEditLayout && (
          // `no-drag` keeps a press on the button from lifting the row (see cancelDrag).
          <span className={classNames(classes.hideControl, 'no-drag')}>
            <Tooltip content={hidden ? 'Unhide product' : 'Hide product'} placement="left" hideIcon disableWrapper>
              <IconButton
                size="small"
                className={classes.hideButton}
                aria-label={`${hidden ? 'Unhide' : 'Hide'} ${item.label}`}
                onClick={() => handleToggleHidden(item, !hidden)}
              >
                {hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </Tooltip>
          </span>
        )}
      </SortableRow>
    )
  }

  // In presentation/customer contexts a collapsed section is simply gone; the editor keeps the header
  // so the Hide/Show toggle stays reachable.
  if (inPresentation && !show) {
    return null
  }

  return (
    <div className={classes.root}>
      <OutlinedTableCard
        title={'Products'}
        headerVariant={'light'}
        singleBorder
        // When there's nothing to show (collapsed or no products), drop the header's bottom divider so
        // it doesn't leave a stray border stacked on the card's bottom edge.
        hideHeaderDivider={!show}
        headerRight={
          // Nothing to hide until a product is listed (ENG-1478).
          isEditable && !inPresentation && (items.length > 0 || isTemplate) ? (
            <HideAmountButton visible={!collapsed} visibleLabel="Hide" hiddenLabel="Show" onToggle={onToggle} />
          ) : undefined
        }
      >
        <Collapse show={show}>
          {isTemplate ? (
            <div className={classes.templateNote}>Set by your settings and estimate type</div>
          ) : (
            <>
              {showSWHeader && (
                <div className={classes.groupHeader}>
                  <Typography component="span" className={classes.groupLabel}>
                    Sherwin-Williams
                  </Typography>
                  <img className={classes.logo} src={isXs ? SW_LOGO_XS : SW_LOGO} alt={'Sherwin-Williams'} />
                </div>
              )}

              <SortableRows
                className={classes.rows}
                updateBeforeSortStart={() => {
                  // the press delay leaves time for a selection to start before the drag does (ENG-2000)
                  window.getSelection?.()?.removeAllRanges()
                  setDragging(true)
                }}
                onSortEnd={handleSortEnd}
                helperClass={`${classes.draggingHelper} draggingHelper`}
                lockAxis={'y'}
                pressDelay={150}
                shouldCancelStart={cancelDrag}
                useWindowAsScrollContainer={true}
              >
                {visibleItems.map((item, index) => renderRow(item, index))}
              </SortableRows>

              {items.length > 3 && (
                <div className={classes.viewMore}>
                  <Button
                    variant={'text'}
                    className={classes.viewMoreButton}
                    onClick={() => setShowAll((prev) => !prev)}
                  >
                    {showAll ? 'View Less' : 'View More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </Collapse>
      </OutlinedTableCard>
    </div>
  )
}

/** A press on the hide control (or anything else marked `no-drag`) must not lift the row. */
function cancelDrag(event: React.SyntheticEvent | Event) {
  const target = event.target as Element | null
  return !!target?.closest?.('.no-drag')
}

interface RowsProps {
  className?: string
  children?: React.ReactNode
}

function Rows({ className, children }: RowsProps) {
  return <div className={className}>{children}</div>
}

interface RowProps {
  className?: string
  children?: React.ReactNode
}

// The row div is the sortable node itself, so each one is a direct child of the positioned `Rows`.
function Row({ className, children }: RowProps) {
  return <div className={className}>{children}</div>
}

const SortableRows: React.ComponentClass<RowsProps & SortableContainerProps, any> = SortableContainer(Rows)
const SortableRow: React.ComponentClass<RowProps & SortableElementProps, any> = SortableElement(Row)
